import { css, cx } from '@emotion/css'
import React, { useMemo } from 'react'
import { useQuery } from 'react-query'
import { VrModuleTitle, VrStandaloneModulePanel } from '../layout'
import { Link } from '@contrast-security-inc/design-system-typography'
import { FormattedMessage } from 'react-intl'
import { HTTPError } from '../../../../api/fetch/decorate-fetch'
import { Card } from '@contrast-security-inc/design-system-surfaces'

export const withEllipsis = (rawText: string, maxLen: number): string => {
  let result = ''
  const titleParts = rawText.split(' ')

  if (titleParts.length === 1) {
    if (titleParts[0].length === 0) {
      return result
    }

    if (titleParts[0].length < maxLen) {
      return titleParts[0]
    }

    return titleParts[0].substring(0, maxLen - 3) + '...'
  }

  for (const part of titleParts) {
    if (result.length + part.length + 1 <= maxLen) {
      result += part + ' '
    } else {
      result += '...'
      break
    }
  }
  return result
}

export function VrModuleHeadlines() {
  const { error, data } = useQuery({
    queryKey: ['rss_feed'],
    queryFn: () =>
      fetch(
        'https://www.contrastsecurity.com/security-influencers/rss.xml'
      ).then((res) =>
        res.text().then((text) => {
          const doc = new DOMParser().parseFromString(text || '', 'text/xml')
          const errorNode = doc.querySelector('parsererror')
          if (errorNode) {
            throw new HTTPError('Cannot parse RSS xml', 400, {
              success: false,
              messages: []
            })
          } else {
            return doc.querySelectorAll('item')
          }
        })
      )
  })

  const rssItems: any[] = useMemo(() => {
    const MAX_HEADLINES = 3
    const results: any[] = []

    if (!data || !!error || data.length === 0) {
      return results
    }

    data.forEach((item) => {
      if (results.length >= MAX_HEADLINES) {
        return results
      }

      const description = item?.querySelector('description')?.innerHTML
      let imgSrc = ''
      let imgAlt = ''

      // NOTE: This is a hack to get the image src and alt from the description because the rss feed doesn't provide it
      // as XML, but as a string of HTML that DOMParser can't parse.
      if (description) {
        const imgParts =
          /&lt;img.+src=['"](?<src>[^'"]*)['"](?:.+alt=['"](?<alt>[^'"]*))?.+/gi.exec(
            description
          )
        if (imgParts?.groups) {
          imgSrc = imgParts.groups.src ?? ''
          imgAlt = imgParts.groups.alt ?? ''
        }
      }

      const TITLE_MAX_LEN = 56
      const title = withEllipsis(
        item?.querySelector('title')?.innerHTML ?? '',
        TITLE_MAX_LEN
      )

      results.push({
        title,
        link: item?.querySelector('link')?.innerHTML ?? '',
        imgSrc,
        imgAlt,
        pubDate: item?.querySelector('pubDate')?.innerHTML ?? ''
      })
    })

    return results
  }, [data, error])

  if (rssItems.length === 0) {
    return null
  }

  return (
    <VrStandaloneModulePanel
      className={css`
        overflow: visible;
      `}
      data-testid="appsec-observer-headlines"
      hasBorder
      flex="1"
      order={1}
      minHeight={150}
    >
      <VrModuleTitle id="title_appsecObserverHeadlines" />
      <div
        className={cx(
          css`
            width: 100%;
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-evenly;
            align-items: flex-start;
          `
        )}
      >
        {rssItems.map((item) => (
          <Card
            key={item.title.replaceAll(' ', '_')}
            className={cx(
              css`
                width: 30%;
                height: auto;
                display: inline-block;
                margin-right: 5px;
                padding: 0px;
                cursor: default;
              `
            )}
          >
            <Link
              hideExternalIcon
              isExternal={true}
              href={item.link}
              className={css`
                display: inline;
              `}
            >
              <img
                src={item.imgSrc}
                alt={item.imgAlt}
                className={cx(
                  css`
                    width: 100%;
                    border-top-left-radius: 4px;
                    border-top-right-radius: 4px;
                    opacity: 85%;
                  `
                )}
              />
              <p
                className={cx(
                  css`
                    padding: 10px;
                    margin-bottom: 3px;
                  `
                )}
              >
                {item.title}
              </p>
              <p
                className={cx(
                  css`
                    padding-left: 10px;
                    padding-right: 10px;
                  `
                )}
              >
                <span
                  className={cx(
                    css`
                      color: gray;
                    `
                  )}
                >
                  {new Date(item.pubDate).toDateString()}
                </span>
              </p>
            </Link>
          </Card>
        ))}
      </div>
      <div
        className={cx(
          css`
            display: block;
            float: right;
            width: 105px;
            padding-top: 35px;
            padding-left: 15px;
          `
        )}
      >
        <Link
          href="https://www.contrastsecurity.com/security-influencers"
          isExternal
          hideExternalIcon
        >
          <FormattedMessage id="detail_appsecObserverHeadlines_moreNews" />
        </Link>
      </div>
    </VrStandaloneModulePanel>
  )
}
